import React, { useId, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { ChevronUpIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import Link from 'next/link';

type CollapsableAlertProps = {
  title?: string;
  description: React.ReactNode | string;
  defaultIsOpen?: boolean;
  icon?: React.ReactNode;
  ctaText?: string;
  ctaUrl?: string;
};

export function CollapsableAlert({
  title,
  description,
  defaultIsOpen = true,
  icon = <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 text-primary-100" />,
  ctaText,
  ctaUrl,
}: CollapsableAlertProps) {
  const [isOpen, setIsOpen] = useState(defaultIsOpen);
  const contentId = useId();

  const toggleOpen = () => setIsOpen((prevState) => !prevState);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      toggleOpen();
    }
  };

  return (
    <div className="mt-4 rounded-lg bg-primary-20 p-4" role="region">
      <div className="flex">
        <div className="mr-2 mt-[1px] flex-shrink-0" aria-hidden="true">
          {icon}
        </div>
        <div className="flex-grow text-left">
          {title && (
            <div
              className="flex cursor-pointer justify-center"
              role="button"
              tabIndex={0}
              aria-expanded={isOpen}
              aria-controls={contentId}
              onClick={toggleOpen}
              onKeyDown={handleKeyDown}
            >
              <h3 className="font-bold">{title}</h3>
              <ChevronUpIcon
                aria-hidden="true"
                className={clsx(
                  'relative -mb-1 -mr-0.5 ml-auto inline-block h-4 w-4 text-neutral-600',
                  {
                    'rotate-180': !isOpen,
                  },
                )}
              />
            </div>
          )}

          <div id={contentId} className={clsx({ 'mr-5 mt-2': title, hidden: !isOpen })}>
            {description}
            {ctaText && ctaUrl && (
              <div className="mt-3">
                <Link
                  href={ctaUrl}
                  className="block w-full rounded-lg border border-neutral-900 px-6 py-3 text-center text-sm font-bold uppercase"
                >
                  {ctaText}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
